// src/servicios/store.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fragmento clave de verificación de roles
  const verificarRolYEstado = async (user) => {
    try {
      if (!user) return null;
  
      // Validar si es restauración de sesión controlada
      const isRestoring = sessionStorage.getItem('isRestoringSession');
      if (isRestoring === 'true') {
        const adminData = JSON.parse(sessionStorage.getItem('originalAdminData'));
        
        // Validación de seguridad adicional
        if (!adminData || Date.now() - adminData.timestamp > 5000) {
          console.error('[Restauración] Datos inválidos o expirados');
          await signOut(auth);
          return null;
        }
  
        // Verificar coincidencia con usuario real
        if (auth.currentUser?.uid !== adminData.uid) {
          console.error('[Restauración] UID no coincide');
          await signOut(auth);
          return null;
        }
  
        return { 
          uid: adminData.uid, 
          email: adminData.email, 
          rol: 'superadmin', 
          estado: 'activo' 
        };
      }
  
      // Verificación normal de roles
      const token = await user.getIdTokenResult(true);
      const uid = user.uid;
  
      // 1. Verificación superadmin mejorada
      const adminDoc = await getDoc(doc(db, 'administradores', uid));
      const esSuperadmin = token.claims.superadmin || adminDoc.exists();
  
      if (esSuperadmin) {
        if (adminDoc.exists()) {
          if (adminDoc.data().estado !== 'activo') {
            await signOut(auth);
            return null;
          }
          // Actualizar datos locales del admin
          return { 
            uid, 
            email: user.email, 
            rol: 'superadmin', 
            estado: 'activo',
            ...adminDoc.data() 
          };
        }
        return { uid, email: user.email, rol: 'superadmin', estado: 'activo' };
      }
  
      // 2. Verificación usuario normal con timeout
      const usuarioDoc = await Promise.race([
        getDoc(doc(db, 'usuarios', uid)),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout de verificación')), 3000)
        )
      ]);
  
      if (usuarioDoc.exists() && usuarioDoc.data().estado === 'activo') {
        return { ...usuarioDoc.data(), uid, rol: 'usuario' };
      }
  
      await signOut(auth);
      return null;
  
    } catch (error) {
      console.error("Error en verificación:", error);
      // Limpiar estados potencialmente corruptos
      sessionStorage.removeItem('isRestoringSession');
      sessionStorage.removeItem('originalAdminData');
      await signOut(auth);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      
      // Restauración de sesión solo durante operaciones críticas
      if (sessionStorage.getItem('isRestoringSession') === 'true') {
        const adminData = JSON.parse(sessionStorage.getItem('originalAdminData'));
        const usuarioValidado = await verificarRolYEstado(adminData);
        setCurrentUser(usuarioValidado);
        return;
      }
  
      try {
        if (user) {
          const usuarioValidado = await verificarRolYEstado(user);
          setCurrentUser(usuarioValidado);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Error en observer:", error);
        setCurrentUser(null);
      }
      setLoading(false);
    });
  
    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      // Limpiar todos los flags
      sessionStorage.removeItem('isRestoringSession');
      sessionStorage.removeItem('originalAdminData');
      sessionStorage.removeItem('needsRefresh');
      // Redirigir SIN recargar
      navigate('/inicio', { replace: true });
    } catch (error) {
      console.error("Error durante logout:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);